import { ModalContextProvider, ModuleLayout, SkeletonText, Space } from '@billy/design-system'
import { useEffect } from 'react'
import { useParams } from 'react-router'

import { useFetchContactById } from '@features/contacts/hooks/useFetchContactById'
import { useFetchShippingAddress } from '@features/contacts/hooks/useFetchShippingAddress'

import { useSegment } from '../../../../hooks'
import { useOrganizationViewUrl } from '../../../../hooks/routing/useOrganizationViewUrl'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { ContactDetailContextProvider } from './contexts/contactDetailContext'
import { ClientDocuments } from './elements/ClientDocuments'
import { ClientInformation } from './elements/ClientInformation'
import { ContactDetailSideActions } from './elements/ContactDetailSideActions'
import { ContactDetailSkeleton } from './elements/ContactDetailSkeleton'
import { ContactPersonsTable } from './elements/ContactPersonsTable'
import { usePrefetchClientDocumentsSummary } from './hooks/usePrefetchClientDocumentsSummary'
import { usePrefetchClientInvoices } from './hooks/usePrefetchClientInvoices'

interface ContactUrlParams {
  clientId: string
}

// react-router-v5-compat's useParams<T>() constrains T to Record<string, string | undefined>,
// which a named interface never structurally satisfies (only type aliases/object literals do).
// Pick<T, keyof T> re-derives the same shape as a mapped type, which does satisfy it.
type ContactUrlParamsGeneric = Pick<ContactUrlParams, keyof ContactUrlParams>

interface ContactDetailProps {
  isEditing?: boolean
}

export const ContactDetail = ({ isEditing = false }: ContactDetailProps) => {
  const { track } = useSegment()
  const { clientId } = useParams<ContactUrlParamsGeneric>()
  const { contact, isLoading } = useFetchContactById(clientId)
  const { shippingAddress, isLoading: isLoadingShippingAddress } = useFetchShippingAddress(contact?.shippingAddressId)
  const { url: clientsUrl } = useOrganizationViewUrl(reactRoute.organization.clients.getMainRoute())
  const { isLoading: isLoadingDocuments } = usePrefetchClientInvoices(contact?.externalId)
  const { isLoading: isLoadingDocumentsSummary } = usePrefetchClientDocumentsSummary(contact?.externalId)

  const isInitialLoading =
    isLoading ||
    (!!contact?.shippingAddressId && isLoadingShippingAddress && !shippingAddress) ||
    isLoadingDocuments ||
    isLoadingDocumentsSummary
  const title = isInitialLoading ? <SkeletonText variant="h1" width={200} /> : contact?.name

  useEffect(() => {
    track('XXX - Invoicing Commerce - Client Details - Viewed')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    // Scopes modal state to this page: leaving with a modal still open (e.g. browser Back) must not
    // reopen a modal with the same id, such as the clients list's create `CustomerModal`.
    <ModalContextProvider>
      <ModuleLayout
        title={title}
        withBackButton
        backButtonTo={clientsUrl}
        sideActions={<ContactDetailSideActions contact={contact} isEditing={isEditing} />}
      >
        {isInitialLoading && <ContactDetailSkeleton />}
        {!isInitialLoading && contact && (
          <ContactDetailContextProvider contact={contact} shippingAddress={shippingAddress}>
            <ClientInformation />
            <Space />
            <ContactPersonsTable />
            <ClientDocuments />
          </ContactDetailContextProvider>
        )}
      </ModuleLayout>
    </ModalContextProvider>
  )
}
